import { useNavigate } from "react-router-dom";
import { Leaf, ArrowLeft } from "lucide-react";

const COMPANY = "Vivi Verde Srl";
const ADDRESS = "Piazza del Popolo, 18 — 00187 Roma";
const VAT = "01454640556";
const EMAIL = "vivi.verdesrl@icloud.com";

function Shell({ title, updated, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <header className="border-b border-stone-200/70 bg-[#FAFAF9]/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/welcome")} className="flex items-center gap-2 font-serif text-2xl"><Leaf size={22} className="text-brand" /> QCOST</button>
          <button data-testid="legal-back" onClick={() => navigate("/welcome")} className="text-sm font-semibold text-stone-600 hover:text-stone-900 flex items-center gap-1.5 transition-colors"><ArrowLeft size={16} /> Torna al sito</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <h1 className="font-serif text-4xl sm:text-5xl font-light">{title}</h1>
        <p className="text-sm text-stone-400 mt-3">Ultimo aggiornamento: {updated}</p>
        <div className="mt-10 space-y-8 text-sm text-stone-600 leading-relaxed">{children}</div>
      </main>
      <footer className="border-t border-stone-200 py-8">
        <p className="max-w-3xl mx-auto px-6 sm:px-10 text-xs text-stone-400">© {new Date().getFullYear()} QCOST · {COMPANY} · P.Iva {VAT}</p>
      </footer>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-serif text-2xl text-stone-900">{children}</h2>;
const Sec = ({ title, children }) => <section><H>{title}</H><div className="mt-2 space-y-2">{children}</div></section>;

export function PrivacyPolicy() {
  return (
    <Shell title="Privacy Policy" updated="Giugno 2026">
      <p>La presente informativa descrive come <b>{COMPANY}</b> ({ADDRESS} — P.Iva {VAT}), in qualità di Titolare del trattamento, tratta i dati personali degli utenti del servizio QCOST, ai sensi del Regolamento (UE) 2016/679 (GDPR).</p>
      <Sec title="Titolare del trattamento">
        <p>{COMPANY} — {ADDRESS}. Per qualsiasi richiesta relativa ai tuoi dati puoi scrivere a <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a>.</p>
      </Sec>
      <Sec title="Dati raccolti">
        <p>Trattiamo i dati che fornisci in fase di registrazione e utilizzo del servizio: nome, indirizzo email, dati dell'azienda (ragione sociale, indirizzo, partita IVA), oltre ai dati gestionali che carichi (fatture elettroniche, vendite, prodotti). Raccogliamo inoltre dati tecnici di navigazione necessari al funzionamento della piattaforma.</p>
      </Sec>
      <Sec title="Finalità e base giuridica">
        <p>I dati sono trattati per: erogare il servizio e gestire l'account (esecuzione del contratto), inviare comunicazioni di servizio e avvisi (legittimo interesse/contratto), adempiere agli obblighi di legge e fiscali, e gestire i pagamenti degli abbonamenti.</p>
      </Sec>
      <Sec title="Modalità e conservazione">
        <p>I dati sono trattati con strumenti informatici e misure di sicurezza adeguate. Ogni azienda accede esclusivamente ai propri dati, isolati dagli altri account. I dati sono conservati per il tempo necessario a fornire il servizio e secondo gli obblighi di legge, poi cancellati o resi anonimi.</p>
      </Sec>
      <Sec title="Fornitori e terze parti">
        <p>Per erogare il servizio ci avvaliamo di fornitori (hosting, invio email, elaborazione pagamenti) che agiscono come Responsabili del trattamento. I pagamenti sono gestiti dal provider di pagamento; non conserviamo i dati completi della tua carta.</p>
      </Sec>
      <Sec title="Diritti dell'interessato">
        <p>Puoi esercitare in ogni momento i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione, oltre al diritto di reclamo al Garante per la protezione dei dati personali, scrivendo a <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a>.</p>
      </Sec>
    </Shell>
  );
}

export function CookiePolicy() {
  return (
    <Shell title="Cookie Policy" updated="Giugno 2026">
      <p>La presente Cookie Policy spiega come <b>{COMPANY}</b> ({ADDRESS} — P.Iva {VAT}) utilizza i cookie e tecnologie simili sul servizio QCOST.</p>
      <Sec title="Cosa sono i cookie">
        <p>I cookie sono piccoli file di testo che i siti salvano sul dispositivo dell'utente per consentirne il funzionamento e per raccogliere informazioni.</p>
      </Sec>
      <Sec title="Cookie tecnici">
        <p>QCOST utilizza esclusivamente <b>cookie tecnici e di sessione</b> necessari al funzionamento del servizio, in particolare per mantenere l'autenticazione dell'utente (token di sessione) dopo l'accesso. Questi cookie non richiedono consenso in quanto indispensabili all'erogazione del servizio richiesto.</p>
      </Sec>
      <Sec title="Cookie di profilazione e di terze parti">
        <p>Il servizio <b>non utilizza cookie di profilazione</b> né cookie pubblicitari di terze parti per tracciare gli utenti a fini di marketing.</p>
      </Sec>
      <Sec title="Gestione dei cookie">
        <p>Puoi gestire o eliminare i cookie tramite le impostazioni del tuo browser. La disabilitazione dei cookie tecnici può però compromettere l'accesso e il corretto funzionamento della piattaforma.</p>
      </Sec>
      <Sec title="Contatti">
        <p>Per domande su questa Cookie Policy scrivi a <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a>.</p>
      </Sec>
    </Shell>
  );
}

export function TermsOfService() {
  return (
    <Shell title="Termini e Condizioni" updated="Giugno 2026">
      <p>I presenti Termini e Condizioni regolano l'utilizzo del servizio QCOST, erogato da <b>{COMPANY}</b> ({ADDRESS} — P.Iva {VAT}). Registrandoti o utilizzando il servizio accetti integralmente i presenti Termini.</p>
      <Sec title="1. Oggetto del servizio">
        <p>QCOST è una piattaforma software (SaaS) per il controllo dei costi di bar e ristoranti che consente di importare fatture elettroniche, monitorare i prezzi dei fornitori, calcolare la marginalità e gestire vendite e prodotti.</p>
      </Sec>
      <Sec title="2. Account e responsabilità">
        <p>Per utilizzare il servizio è necessario creare un account fornendo dati veritieri e aggiornati. Sei responsabile della riservatezza delle credenziali di accesso e di tutte le attività svolte tramite il tuo account. Ogni azienda accede esclusivamente ai propri dati.</p>
      </Sec>
      <Sec title="3. Periodo di prova e abbonamenti">
        <p>Il servizio prevede un periodo di prova gratuito di 15 giorni, senza obbligo di carta di credito. Al termine della prova puoi sottoscrivere uno dei piani a pagamento. Gli abbonamenti si rinnovano secondo la periodicità scelta (mensile o annuale) e restano attivi fino alla disdetta.</p>
      </Sec>
      <Sec title="4. Pagamenti">
        <p>I pagamenti sono gestiti tramite un provider di pagamento esterno. I corrispettivi sono indicati al netto o comprensivi delle imposte come specificato in fase di acquisto. Salvo diversa previsione di legge, gli importi versati non sono rimborsabili per i periodi già fruiti.</p>
      </Sec>
      <Sec title="5. Uso corretto">
        <p>Ti impegni a non utilizzare il servizio per finalità illecite, a non tentare di comprometterne la sicurezza o l'integrità e a non caricare contenuti di cui non hai la titolarità o il diritto di trattamento.</p>
      </Sec>
      <Sec title="6. Disponibilità e limitazione di responsabilità">
        <p>Il servizio è fornito "così com'è". {COMPANY} si impegna a garantire la migliore continuità possibile ma non risponde di interruzioni dovute a manutenzioni, cause tecniche o eventi al di fuori del proprio controllo. Nei limiti consentiti dalla legge, la responsabilità è limitata all'importo corrisposto per il servizio.</p>
      </Sec>
      <Sec title="7. Recesso e cessazione">
        <p>Puoi disdire l'abbonamento in qualsiasi momento dalla sezione Abbonamento; la cessazione ha effetto al termine del periodo già pagato. {COMPANY} può sospendere o chiudere account in caso di violazione dei presenti Termini.</p>
      </Sec>
      <Sec title="8. Modifiche ai Termini">
        <p>{COMPANY} può aggiornare i presenti Termini; le modifiche saranno comunicate tramite il servizio e si applicheranno dalla loro pubblicazione.</p>
      </Sec>
      <Sec title="9. Legge applicabile e foro competente">
        <p>I presenti Termini sono regolati dalla legge italiana. Per ogni controversia è competente il foro del luogo in cui ha sede il Titolare, salvo i fori inderogabili previsti a tutela del consumatore.</p>
      </Sec>
      <Sec title="10. Contatti">
        <p>Per qualsiasi domanda relativa ai presenti Termini scrivi a <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a>.</p>
      </Sec>
    </Shell>
  );
}
